import React, { useEffect, useState } from "react";
import axios from "axios";
import FrontendLayout from "../../../components/frontend/FrontendLayout";
import UserCommon from "../../../components/frontend/UserCommon";
import UserTabs from "../../../components/frontend/UserTabs";
import { ToastContainer, toast } from "react-toastify";
import { useUserProfile } from "../../../hooks/useUserProfile";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface DashboardStats {
  totalListings: number;
  maxListings: number;
  pendingListings: number;
  favorites: number;
  reviews: number;
  enquiries: number;
}

interface MonthlyData {
  month: string;
  sales: number;
}

interface PlotData {
  name: string;
  revenue: number;
  color: string;
}

const UserDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalListings: 0,
    maxListings: 50,
    pendingListings: 0,
    favorites: 0,
    reviews: 0,
    enquiries: 0,
  });

  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [plotData, setPlotData] = useState<PlotData[]>([]);

  const token = localStorage.getItem("frontendUserToken");
  const userType = localStorage.getItem("frontendUserType");
  const profile = useUserProfile(token);

  useEffect(() => {
    if (!profile?.id) return;

    const fetchDashboardData = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/dashboard/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data.success) {
          const apiStats = res.data.stats;

          setStats({
            totalListings: apiStats.yourListing,
            maxListings: 50,
            pendingListings: apiStats.pending,
            favorites: apiStats.favorites,
            reviews: apiStats.viewed,
            enquiries: apiStats.enquiries,
          });
        } else {
          toast.error(res.data.message || "Failed to fetch dashboard data.");
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setStats({
          totalListings: 32,
          maxListings: 50,
          pendingListings: 2,
          favorites: 6,
          reviews: 1483,
          enquiries: 0,
        });
      }
    };

    const fetchChartData = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/dashboard/chart-data`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data.success) {
          setMonthlyData(res.data.chartData.monthlyData);
          setPlotData(res.data.chartData.plotData);
        } else {
          toast.error(res.data.message || "Failed to fetch chart data.");
        }
      } catch (err) {
        console.error("Error fetching chart data:", err);
        // Fallback data in case of error
        setMonthlyData([
          { month: "Jan", sales: 1200 },
          { month: "Feb", sales: 1900 },
          { month: "Mar", sales: 3000 },
          { month: "Apr", sales: 2500 },
          { month: "May", sales: 3200 },
          { month: "Jun", sales: 4100 },
        ]);
        setPlotData([
          {
            name: "Favorites",
            revenue: 3000,
            color: "rgba(255, 99, 132, 0.7)",
          },
          { name: "Views", revenue: 2500, color: "rgba(54, 162, 235, 0.7)" },
          {
            name: "Enquiries",
            revenue: 2000,
            color: "rgba(255, 206, 86, 0.7)",
          },
        ]);
      }
    };

    fetchDashboardData();
    fetchChartData();
  }, [token, profile?.id]);

  useEffect(() => {
    const initCharts = () => {
      const Chart = (window as any).Chart;

      if (!Chart) {
        console.warn(
          "Chart.js not loaded. Please include Chart.js CDN in your HTML."
        );
        return;
      }

      const salesCtx = document.getElementById(
        "salesChart"
      ) as HTMLCanvasElement;
      if (salesCtx) {
        new Chart(salesCtx, {
          type: "line",
          data: {
            labels: monthlyData.map((item) => item.month),
            datasets: [
              {
                label: "Monthly Enquiries",
                data: monthlyData.map((item) => item.sales),
                borderColor: "rgba(75, 192, 192, 1)",
                backgroundColor: "rgba(75, 192, 192, 0.2)",
                borderWidth: 2,
                tension: 0.4,
                fill: true,
              },
            ],
          },
          options: {
            responsive: true,
            plugins: {
              legend: { position: "top" },
            },
          },
        });
      }

      const pieCtx = document.getElementById("pieChart") as HTMLCanvasElement;
      if (pieCtx) {
        new Chart(pieCtx, {
          type: "pie",
          data: {
            labels: plotData.map((item) => item.name),
            datasets: [
              {
                label: "Revenue Share",
                data: plotData.map((item) => item.revenue),
                backgroundColor: plotData.map((item) => item.color),
                borderColor: ["#fff"],
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            plugins: {
              legend: { position: "bottom" },
            },
          },
        });
      }
    };

    const timer = setTimeout(initCharts, 500);
    return () => clearTimeout(timer);
  }, [monthlyData, plotData]);

  return (
    <FrontendLayout>
      <ToastContainer />
      <div className="user">
        <div className="tf-container py-5 user">
          <UserCommon
            name={profile?.name}
            email={profile?.email}
            imageUrl={profile?.imageUrl}
            userType={profile?.userType}
          />

          <div className="tab" role="tabpanel">
            <UserTabs />

            <div className="tab-content tabs">
              <div role="tabpanel" className="tab-pane in active">
                <div className="row">
                  {(userType === "1" || userType === "2") && (
                    <>
                      <div className="col-sm-3 ps-md-0 pt-4">
                        <div className="flat-counter-v2 tf-counter">
                          <div className="counter-box sec1">
                            <div className="box-icon">
                              <span className="icon">
                                <i className="fas fa-list-ul"></i>
                              </span>
                            </div>
                            <div className="content-box">
                              <div className="title-count text-variant-1">
                                Your listing
                              </div>
                              <div className="box-count d-flex align-items-end">
                                <div className="number">
                                  {stats.totalListings}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-sm-3 ps-md-0 pt-4">
                        <div className="flat-counter-v2 tf-counter">
                          <div className="counter-box sec2">
                            <div className="box-icon">
                              <span className="icon">
                                <i className="fas fa-hourglass-half"></i>
                              </span>
                            </div>
                            <div className="content-box">
                              <div className="title-count text-variant-1">
                                Pending
                              </div>
                              <div className="box-count d-flex align-items-end">
                                <div className="number">
                                  {stats.pendingListings}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-sm-3 ps-md-0 pt-4">
                        <div className="flat-counter-v2 tf-counter">
                          <div className="counter-box sec3">
                            <div className="box-icon">
                              <span className="icon">
                                <i className="fa-regular fa-heart"></i>
                              </span>
                            </div>
                            <div className="content-box">
                              <div className="title-count text-variant-1">
                                Favorites
                              </div>
                              <div className="d-flex align-items-end">
                                <div className="number">{stats.favorites}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-sm-3 ps-md-0 pt-4">
                        <div className="flat-counter-v2 tf-counter">
                          <div className="counter-box sec5">
                            <div className="box-icon">
                              <span className="icon">
                                <i className="fa-regular fa-envelope"></i>
                              </span>
                            </div>
                            <div className="content-box">
                              <div className="title-count text-variant-1">
                                Enquiries
                              </div>
                              <div className="d-flex align-items-end">
                                <div className="number">{stats.enquiries}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {userType === "3" && (
                    <>
                      <div className="col-sm-4 ps-md-0 pt-4">
                        <div className="flat-counter-v2 tf-counter">
                          <div className="counter-box sec3">
                            <div className="box-icon">
                              <span className="icon">
                                <i className="fa-regular fa-heart"></i>
                              </span>
                            </div>
                            <div className="content-box">
                              <div className="title-count text-variant-1">
                                Favorites
                              </div>
                              <div className="d-flex align-items-end">
                                <div className="number">{stats.favorites}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-sm-4 ps-md-0 pt-4">
                        <div className="flat-counter-v2 tf-counter">
                          <div className="counter-box sec4">
                            <div className="box-icon">
                              <span className="icon">
                                <i className="fa-regular fa-star"></i>
                              </span>
                            </div>
                            <div className="content-box">
                              <div className="title-count text-variant-1">
                                Views
                              </div>
                              <div className="d-flex align-items-end">
                                <div className="number">
                                  {stats.reviews.toLocaleString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-sm-4 ps-md-0 pt-4">
                        <div className="flat-counter-v2 tf-counter">
                          <div className="counter-box sec5">
                            <div className="box-icon">
                              <span className="icon">
                                <i className="fa-regular fa-envelope"></i>
                              </span>
                            </div>
                            <div className="content-box">
                              <div className="title-count text-variant-1">
                                Enquiries
                              </div>
                              <div className="d-flex align-items-end">
                                <div className="number">{stats.enquiries}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {(userType === "1" || userType === "2") && (
                  <>
                    <div className="row">
                      <div className="col-sm-7 pt-4">
                        <canvas id="salesChart" height="200"></canvas>
                      </div>
                      <div className="col-sm-5 pt-4">
                        <canvas id="pieChart" height="200"></canvas>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </FrontendLayout>
  );
};

export default UserDashboard;
